import { useCallback, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  styled,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { DeleteOutlineOutlined } from "@mui/icons-material";
import {
  Body,
  BottomActionArea,
  BottomActionButton,
  DigitalSign,
  FullScreenLoader,
  Header,
  PatientSummaryAccordion,
  SingleFormInput,
  scrollToTop,
  NavigateBeforeButton,
  TextAreaFormInput,
  ExtraWhiteSpace,
  AuthRequiredModal,
  InputFieldWrapper,
  ContentContainer as BaseContentContainer,
  PatientSummary as BasePatientSummary,
} from "@components";
import {
  DocumentCreationLoaderTitle,
  MedicalOrder,
  Patient,
  RequestStatus,
  Suggestion,
  RequestType,
  patientDefaultValues,
} from "@interfaces";
import { useDoctorContext } from "@contexts";
import { useCreateDocument, useRequestData } from "@hooks";
import { theme } from "@config";
import { eraseProcedureCode } from "./utils/parse";
import { DateField } from "@mui/x-date-pickers/DateField";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { esES } from "@mui/x-date-pickers/locales";
import es from "date-fns/locale/es";

const ContentContainer = styled(BaseContentContainer)(({ theme }) => ({
  gap: 24,
}));

const PatientSummary = styled(BasePatientSummary)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const HeaderContainer = styled(Body)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  width: "100%",
  paddingBottom: 0,
  justifyContent: "center",
  [theme.breakpoints.up("md")]: {
    justifyContent: "space-between",
    padding: 24,
    paddingBottom: 0,
    maxWidth: 1200,
  },
  [theme.breakpoints.down("md")]: {
    paddingBottom: 0,
  },
}));

const Content = styled(Body)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  width: "100%",
  alignItems: "normal",
  [theme.breakpoints.up("md")]: {
    padding: 24,
    maxWidth: 1200,
    paddingTop: 0,
    paddingBottom: 0,
  },
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    alignItems: "center",
    paddingTop: 0,
  },
}));

const PatientContent = styled(Box)(({ theme }) => ({
  width: "100%",
  [theme.breakpoints.up("md")]: {
    maxWidth: 328,
  },
}));

const PrescriptionContent = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 16,
  width: "100%",
  paddingTop: 0,
}));

const PatientSummaryAccordionContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  [theme.breakpoints.up("md")]: {
    display: "none",
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const AddMedicineButton = styled(Button)(({ theme }) => ({
  display: "flex",
  color: theme.palette.primary.dark,
  backgroundColor: theme.palette.primary.extraLight,
  border: `1px solid ${theme.palette.primary.dark}`,
  borderRadius: "20.5px",
  width: "100%",
  textTransform: "none",
  [theme.breakpoints.up("md")]: {
    maxWidth: 240,
  },
}));

const RouterLink = styled(Link)(({ theme }) => ({
  textDecoration: "none",
  [theme.breakpoints.down("md")]: {
    width: "100%",
  },
}));

const StyledProcedureCard = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "8px",
  borderRadius: "4px",
  minHeight: "58px",
  fontSize: "14px",
  backgroundColor: theme.pallete.background.white,
});

interface ProcedureCardProps {
  procedure: Suggestion;
  onClose: () => void;
}

const ProcedureCard = ({ procedure, onClose }: ProcedureCardProps) => {
  return (
    <StyledProcedureCard>
      <Box>{procedure.text}</Box>
      <IconButton onClick={onClose}>
        <DeleteOutlineOutlined color="primary" />
      </IconButton>
    </StyledProcedureCard>
  );
};

const ProceduresTitle = styled(Box)({
  fontSize: "16px",
  fontWeight: 500,
  lineHeight: "23px",
  margin: "16px 0",
  color: theme.pallete.text.black,
});

const ProcedureCardsContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
});

const FormContainer = styled("form")({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  gap: "20px",
  marginTop: "20px",
});

const StyledDateField = styled(DateField)<{ error: boolean }>({
  backgroundColor: theme.pallete.background.white,
  ...(props) =>
    props.error && {
      border: "1px solid red",
      borderRadius: "4px",
    },
});

interface LocationState {
  repeatedDocumentDetails?: Suggestion[];
  repeatedDiagnosticHypothesis?: string;
  repeatedIndications?: string;
  attachedProcedures?: Suggestion[];
}

export function MedicalOrderCreate() {
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );
  const doctor = useDoctorContext();
  const navigate = useNavigate();
  const { requestId = "" } = useParams();
  const { data: requestData, isPending: isPendingRequestData } =
    useRequestData();
  const location = useLocation();
  const {
    repeatedDiagnosticHypothesis,
    repeatedDocumentDetails,
    repeatedIndications,
  }: LocationState = location.state || {};
  const [attachedProcedures, setAttachedProcedures] = useState<Suggestion[]>(
    repeatedDocumentDetails ?? []
  );
  const [showFeaAuthModal, setShowFeaAuthModal] = useState<boolean>(false);
  const [showSummaryView, setShowSummaryView] = useState<boolean>(true);
  const patient: Patient = useMemo(() => {
    return requestData?.patient ?? patientDefaultValues;
  }, [requestData]);

  const isDraft = requestData?.request.status === RequestStatus.DRAFT;
  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors },
  } = useForm<MedicalOrder>({
    defaultValues: {
      documentDetails:
        repeatedDocumentDetails?.map((detail) => ({
          description: detail.text,
          code: detail.code ?? null,
        })) ?? [],
      diagnosticHypothesis: repeatedDiagnosticHypothesis ?? "",
      indications: repeatedIndications ?? "",
      effectiveDate: new Date().toISOString(),
    },
  });

  const { mutate: createDocument, isPending } = useCreateDocument(
    RequestType.MEDICAL_ORDER
  );
  const [showDigitalSign, setShowDigitalSign] = useState(false);
  const diagnosticHypothesis = watch("diagnosticHypothesis");
  const indications = watch("indications");
  const effectiveDate = watch("effectiveDate");

  const onProcedureCardClose = useCallback((procedure: Suggestion) => {
    setAttachedProcedures((prevProcedures) =>
      prevProcedures.filter(
        (prevProcedure) => prevProcedure.text !== procedure.text
      )
    );
  }, []);

  const handleDocumentDataSubmit = useCallback(() => {
    if (!doctor.isFeaAuthenticated) {
      setShowFeaAuthModal(true);
      return;
    }
    setShowDigitalSign(true);
    scrollToTop();
  }, [doctor]);

  const onDigitalSignSubmit = useCallback(async () => {
    setShowDigitalSign(false);
    createDocument({
      document: {
        effectiveDate,
        documentDetails: attachedProcedures.map((procedure) => ({
          description: eraseProcedureCode(procedure.text),
          code: procedure.code ?? null,
        })),
        diagnosticHypothesis: diagnosticHypothesis ?? "",
        indications: indications ?? "",
      },
      managementSummary: attachedProcedures
        .map((procedure) => eraseProcedureCode(procedure.text))
        .join(", "),
    });
  }, [
    createDocument,
    effectiveDate,
    attachedProcedures,
    diagnosticHypothesis,
    indications,
  ]);

  const handleBack = useCallback(() => {
    if (!showSummaryView) {
      setShowSummaryView(true);
      return;
    }
    if (isDraft) {
      navigate(`/app/nuevo-documento/paciente`, {
        state: { documentType: RequestType.MEDICAL_ORDER },
      });
    } else {
      navigate(`/app/solicitudes/${requestId}/gestionar/seleccionar-documento`);
    }
  }, [isDraft, navigate, requestId, showSummaryView]);

  if (showDigitalSign) {
    return (
      <DigitalSign
        documentType={RequestType.MEDICAL_ORDER}
        handleCreateDocument={onDigitalSignSubmit}
        handleBack={() => setShowDigitalSign(false)}
      />
    );
  }

  if (isPendingRequestData) {
    return (
      <>
        <Header
          title={isMobile ? "Resumen orden" : undefined}
          useMekiDocIcon={!isMobile}
          titleColor="black"
          left={<NavigateBeforeButton onClick={handleBack} />}
        />
        <ContentContainer flex={1} disableGutters>
          <Body justifyContent="center">
            <FullScreenLoader title="Cargando información del paciente..." />
          </Body>
        </ContentContainer>
      </>
    );
  }

  if (isPending)
    return (
      <>
        <Header
          title={isMobile ? "Resumen orden" : undefined}
          useMekiDocIcon={!isMobile}
          titleColor="black"
          left={<NavigateBeforeButton onClick={handleBack} />}
        />
        <ContentContainer flex={1} disableGutters>
          <Body justifyContent="center">
            <FullScreenLoader
              title={DocumentCreationLoaderTitle[RequestType.MEDICAL_ORDER]}
            />
          </Body>
        </ContentContainer>
      </>
    );
  return (
    <>
      <Header
        title={isMobile ? "Resumen orden" : undefined}
        useMekiDocIcon={!isMobile}
        titleColor="black"
        left={<NavigateBeforeButton onClick={handleBack} />}
      />
      <ContentContainer disableGutters>
        <HeaderContainer>
          <Title variant="h5" fontWeight={"medium"}>
            Resumen orden
          </Title>
          <RouterLink
            to={"buscar"}
            state={{ attachedProcedures, medicalOrderStarted: true }}
          >
            <AddMedicineButton
              variant="outlined"
              disabled={isPending}
              size="large"
            >
              <Typography variant="body1" fontWeight={"medium"}>
                Agregar exámenes
              </Typography>
            </AddMedicineButton>
          </RouterLink>
        </HeaderContainer>

        <Content>
          <PatientContent>
            <PatientSummary
              patient={patient}
              prevRoute={`/app/solicitudes/${requestId}/orden-medica/crear`}
              requestId={requestId}
              canEdit={isDraft}
            />
            <PatientSummaryAccordionContainer>
              <PatientSummaryAccordion
                patient={patient}
                prevRoute={`/app/solicitudes/${requestId}/orden-medica/crear`}
                requestId={requestId}
                isOpen={isDraft && attachedProcedures.length === 0}
                canEdit={isDraft}
              />
            </PatientSummaryAccordionContainer>
          </PatientContent>
          <PrescriptionContent>
            {showSummaryView ? (
              <>
                {attachedProcedures.length > 0 && (
                  <>
                    <ProceduresTitle>Exámenes y procedimientos</ProceduresTitle>
                    <ProcedureCardsContainer>
                      {attachedProcedures.map((procedure) => (
                        <ProcedureCard
                          key={procedure.text + (procedure.code ?? "")}
                          procedure={procedure}
                          onClose={() => {
                            onProcedureCardClose(procedure);
                          }}
                        />
                      ))}
                    </ProcedureCardsContainer>
                  </>
                )}
                <ExtraWhiteSpace />
                <BottomActionArea>
                  <BottomActionButton
                    onClick={() => {
                      setShowSummaryView(false);
                    }}
                    disabled={attachedProcedures.length === 0}
                  >
                    Siguiente
                  </BottomActionButton>
                </BottomActionArea>
              </>
            ) : (
              <FormContainer onSubmit={handleSubmit(handleDocumentDataSubmit)}>
                <SingleFormInput
                  title="Hipótesis diagnóstica"
                  parameterName="diagnosticHypothesis"
                  register={register}
                  errors={errors}
                />
                <TextAreaFormInput
                  title="Indicaciones"
                  parameterName="indications"
                  register={register}
                  errors={errors}
                  minHeight="80px"
                />
                <InputFieldWrapper
                  label="Fecha de orden"
                  error={errors.effectiveDate?.message}
                >
                  <FormControl fullWidth>
                    <LocalizationProvider
                      dateAdapter={AdapterDateFns}
                      adapterLocale={es}
                      localeText={
                        esES.components.MuiLocalizationProvider.defaultProps
                          .localeText
                      }
                    >
                      <Controller
                        name="effectiveDate"
                        control={control}
                        defaultValue={undefined}
                        rules={{
                          required: "Debes seleccionar una fecha.",
                          validate: (value) => {
                            const date = new Date(value);
                            if (date.toString() === "Invalid Date") {
                              return "Fecha incompleta o inválida";
                            }
                            if (date.getFullYear() < 1900) {
                              return "La fecha es demasiado antigua";
                            }
                            return true;
                          },
                        }}
                        render={({ field: { onChange, value } }) => (
                          <StyledDateField
                            minDate={new Date("1900-01-01")}
                            value={value ? new Date(value) : null}
                            onChange={(date) => onChange(date)}
                            clearable={true}
                            format="dd/MM/yyyy"
                            error={
                              errors.effectiveDate?.message ===
                              "Debes seleccionar una fecha."
                            }
                            slotProps={{
                              // Disable the blue border on focus when there is an error
                              ...(errors.effectiveDate &&
                                errors.effectiveDate.message ===
                                  "Debes seleccionar una fecha." && {
                                  textField: {
                                    focused: false,
                                  },
                                }),
                            }}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </FormControl>
                </InputFieldWrapper>

                <ExtraWhiteSpace />
                <BottomActionArea>
                  <BottomActionButton type="submit">
                    Siguiente
                  </BottomActionButton>
                </BottomActionArea>
              </FormContainer>
            )}
          </PrescriptionContent>
        </Content>
        <AuthRequiredModal
          requestId={requestId}
          isOpen={showFeaAuthModal}
          onClose={() => setShowFeaAuthModal(false)}
        />
      </ContentContainer>
    </>
  );
}
